import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useCopyStrategyToGroups, useStrategyCopyTargets } from '@/hooks/useStrategiesData';
import { ApiError } from '@/providers/ApiConfigProvider';
import type { CollectionStrategyItem } from '@/types/collection-strategies';

interface CopyStrategyToGroupsDialogProps {
  strategy: CollectionStrategyItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CopyStrategyToGroupsDialog({ strategy, open, onOpenChange }: CopyStrategyToGroupsDialogProps) {
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const { data: targets = [], isLoading } = useStrategyCopyTargets(open);
  const copyMutation = useCopyStrategyToGroups();

  const toggleGroup = (companyGroupId: number, checked: boolean) => {
    setSelectedGroupIds((previous) =>
      checked ? [...previous, companyGroupId] : previous.filter((id) => id !== companyGroupId),
    );
  };

  const close = () => {
    setSelectedGroupIds([]);
    onOpenChange(false);
  };

  const groupName = (companyGroupId: number) =>
    targets.find((target) => target.companyGroupId === companyGroupId)?.name ?? `group ${companyGroupId}`;

  const handleCopy = () => {
    if (!strategy || selectedGroupIds.length === 0) return;

    copyMutation.mutate(
      { strategyId: strategy.id, companyGroupIds: selectedGroupIds },
      {
        onSuccess: (response) => {
          const copied = response.results.filter((result) => result.copied);
          const skipped = response.results.filter((result) => !result.copied);

          if (copied.length > 0) {
            toast.success(
              `'${strategy.name}' copied to ${copied.length} company group${copied.length === 1 ? '' : 's'} as inactive`,
            );
          }

          for (const result of skipped) {
            toast.warning(`${groupName(result.companyGroupId)}: ${result.skipReason}`);
          }

          for (const result of copied.filter((x) => x.unresolvedStageTemplates.length > 0)) {
            const stages = result.unresolvedStageTemplates
              .map((stage) => `stage ${stage.sortOrder} ('${stage.templateName}')`)
              .join(', ');
            toast.warning(`${groupName(result.companyGroupId)}: no matching template for ${stages}`);
          }

          close();
        },
        onError: (error) => {
          const message =
            error instanceof ApiError ? error.message : 'An unexpected error occurred while copying the strategy.';
          toast.error(message);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (nextOpen ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Copy to other company groups</DialogTitle>
          <DialogDescription>
            {strategy
              ? `'${strategy.name}' and its stages will be copied as a new, independent inactive strategy. Later edits here will not affect the copies.`
              : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {isLoading && <p className="text-sm text-muted-foreground">Loading company groups…</p>}
          {!isLoading && targets.length === 0 && (
            <p className="text-sm text-muted-foreground">There are no other company groups you can copy into.</p>
          )}
          {targets.map((target) => (
            <div key={target.companyGroupId} className="flex items-center gap-3">
              <Checkbox
                id={`copy-strategy-target-${target.companyGroupId}`}
                checked={selectedGroupIds.includes(target.companyGroupId)}
                onCheckedChange={(checked) => toggleGroup(target.companyGroupId, checked === true)}
              />
              <Label htmlFor={`copy-strategy-target-${target.companyGroupId}`} className="font-normal">
                {target.name}
                <span className="text-muted-foreground ml-2 text-xs">
                  {target.companyCount} compan{target.companyCount === 1 ? 'y' : 'ies'}
                </span>
              </Label>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button onClick={handleCopy} disabled={selectedGroupIds.length === 0 || copyMutation.isPending}>
            {copyMutation.isPending ? 'Copying…' : 'Copy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
